import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import type { CarCatalogEntry } from '@parkproof/shared';
import { Button } from '../components/Button';
import { CarPickerDialog } from '../components/CarPickerDialog';
import { Car as CarIcon, ChevronLeft, ChevronRight } from '../components/Icon';
import { useAuth } from '../contexts/AuthContext';
import { analytics } from '../lib/logger';
import type { Car, CarType } from '../types';

const TYPES: CarType[] = ['sedan', 'suv', 'van', 'pickup', 'coupe', 'wagon'];

// Heights mirror app/src/screens/AddCarScreen.tsx — toggling these updates the
// displayed total; on save we subtract them out so we store the base car height
// plus boolean flags, just like iOS.
const ATTACHMENT_HEIGHTS: Record<string, number> = {
  roof_rack: 0.1,
  roof_box: 0.4,
};

const ATTACHMENT_TAGS = [
  { id: 'roof_rack', label: '+ Roof rack (+0.10 m)' },
  { id: 'roof_box', label: '+ Roof box (+0.40 m)' },
];

const newCarId = () => `car_${Date.now().toString(36)}`;

export default function AddCarPage() {
  const navigate = useNavigate();
  const { firebaseUser, profile, saveCars } = useAuth();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedFromCatalog, setPickedFromCatalog] = useState<CarCatalogEntry | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState<CarType>('suv');
  const [heightStr, setHeightStr] = useState('');
  const [attachments, setAttachments] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Form fields only appear once the user has either picked from the catalog or
  // explicitly chosen the manual path — same as iOS.
  const [formVisible, setFormVisible] = useState(false);

  useEffect(() => {
    if (!firebaseUser) navigate('/signin', { replace: true });
  }, [firebaseUser, navigate]);

  const toggleAttachment = (id: string) => {
    const isAdding = !attachments.includes(id);
    const delta = ATTACHMENT_HEIGHTS[id] ?? 0;
    const current = parseFloat(heightStr.replace(',', '.'));
    if (Number.isFinite(current) && delta !== 0) {
      const next = isAdding ? current + delta : current - delta;
      setHeightStr(next.toFixed(2));
    }
    setAttachments((prev) => (isAdding ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const handlePick = (entry: CarCatalogEntry) => {
    setPickedFromCatalog(entry);
    setName(`${entry.make} ${entry.model}`);
    setType(entry.type);
    setHeightStr(entry.heightM.toFixed(2));
    setAttachments([]);
    setError(null);
    setFormVisible(true);
    setPickerOpen(false);
  };

  const handleManualEntry = () => {
    setPickedFromCatalog(null);
    setName('');
    setHeightStr('');
    setAttachments([]);
    setError(null);
    setFormVisible(true);
    setPickerOpen(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firebaseUser) return;
    const displayedHeight = parseFloat(heightStr.replace(',', '.'));
    if (!Number.isFinite(displayedHeight) || displayedHeight < 1.0 || displayedHeight > 4.0) {
      setError('Enter a height between 1.0 and 4.0 meters.');
      return;
    }
    if (!name.trim()) {
      setError('Give your car a label, e.g. "My Range Rover".');
      return;
    }
    setError(null);
    const extra =
      (attachments.includes('roof_rack') ? ATTACHMENT_HEIGHTS.roof_rack : 0) +
      (attachments.includes('roof_box') ? ATTACHMENT_HEIGHTS.roof_box : 0);
    const heightM = +(displayedHeight - extra).toFixed(2);

    setBusy(true);
    try {
      const newCar: Car = {
        id: newCarId(),
        label: name.trim(),
        type,
        heightM,
        hasRoofRack: attachments.includes('roof_rack'),
        hasRoofBox: attachments.includes('roof_box'),
      };
      const next = [...(profile?.cars ?? []), newCar];
      await saveCars(next);
      
      // Track car added
      analytics.carAdded({
        carType: newCar.type,
        heightM: newCar.heightM,
        label: newCar.label,
      });
      
      navigate(-1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="px-s4 py-s2">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center"
          aria-label="Back"
        >
          <ChevronLeft size={20} />
        </button>
      </div>

      <form onSubmit={submit} className="p-s5 flex flex-col gap-s5">
        <div>
          <div className="caption text-ink3 mb-s3 font-mono">MY GARAGE</div>
          <h1 className="font-serif text-[30px] leading-[33px] tracking-tight text-ink1">
            Add a <span className="font-serif-italic">car</span>
          </h1>
          <p className="mt-1.5 text-[14px] text-ink2 font-sans leading-snug">
            Pick from the list — we'll auto-fill the height. Set roof rack or roof box defaults
            here; toggle them per trip on the home screen.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className={clsx(
            'flex items-center gap-s4 p-s4 border rounded-md text-left',
            pickedFromCatalog ? 'bg-ink1 border-ink1' : 'bg-card border-rule',
          )}
        >
          <div className="w-11 h-11 rounded-full bg-tint flex items-center justify-center shrink-0">
            <CarIcon size={22} color={pickedFromCatalog ? '#FFFFFF' : '#4D4B47'} />
          </div>
          <div className="flex-1 min-w-0">
            {pickedFromCatalog ? (
              <>
                <div className="font-sans font-semibold text-[10px] tracking-wider text-tint">
                  SELECTED CAR
                </div>
                <div className="font-serif font-medium text-[16px] text-card mt-0.5 truncate">
                  {pickedFromCatalog.make} {pickedFromCatalog.model}
                </div>
                <div className="font-mono text-[11px] tracking-wider text-tint mt-0.5">
                  {pickedFromCatalog.type.toUpperCase()} · {pickedFromCatalog.heightM.toFixed(2)} M
                </div>
              </>
            ) : (
              <>
                <div className="font-sans font-semibold text-[15px] text-ink1">Choose your car</div>
                <div className="font-sans text-[12px] text-ink3 mt-0.5">
                  Pick make and model — height auto-fills
                </div>
              </>
            )}
          </div>
          <ChevronRight size={16} color={pickedFromCatalog ? '#FFFFFF' : '#4D4B47'} />
        </button>

        {!formVisible ? (
          <button
            type="button"
            onClick={handleManualEntry}
            className="self-center text-[12px] font-sans font-medium text-ink2 underline px-s2 py-s1"
          >
            My car isn't listed — enter manually
          </button>
        ) : null}

        {formVisible ? (
          <>
            <div>
              <label className="caption text-ink3 block mb-2">Name</label>
              <div className="bg-card border border-rule rounded-md px-s4 py-3.5">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Family Range Rover"
                  disabled={busy}
                  className="w-full bg-transparent text-[15px] font-sans text-ink1 placeholder-ink3 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="caption text-ink3 block mb-2">Type</label>
              <div className="flex flex-wrap gap-1.5">
                {TYPES.map((t) => {
                  const active = type === t;
                  return (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setType(t)}
                      disabled={busy}
                      className={clsx(
                        'px-4 py-2.5 border rounded-sm text-[13px] font-sans transition-colors basis-[32%] grow text-center',
                        active
                          ? 'bg-ink1 border-ink1 text-card font-medium'
                          : 'bg-card border-rule text-ink1',
                      )}
                    >
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="caption text-ink3 block mb-2">Height</label>
              <div className="bg-card border border-rule rounded-md px-s4 py-3.5 flex items-baseline justify-between">
                <input
                  type="text"
                  inputMode="decimal"
                  value={heightStr}
                  onChange={(e) => setHeightStr(e.target.value)}
                  placeholder="1.85"
                  disabled={busy}
                  className="flex-1 bg-transparent font-mono text-[24px] text-ink1 placeholder-ink3 outline-none"
                />
                <span className="font-sans text-[11px] font-semibold text-ink3 uppercase tracking-widest">
                  M
                </span>
              </div>
              <div className="mt-1.5 text-[11px] text-ink3 font-sans">
                {pickedFromCatalog
                  ? 'Manufacturer spec — adjust if your trim, wheels, or attachments differ.'
                  : 'Total height including any attachments toggled below.'}
              </div>
            </div>

            <div>
              <label className="caption text-ink3 block mb-2">Default attachments</label>
              <div className="flex flex-wrap gap-1.5">
                {ATTACHMENT_TAGS.map((a) => {
                  const active = attachments.includes(a.id);
                  return (
                    <button
                      type="button"
                      key={a.id}
                      onClick={() => toggleAttachment(a.id)}
                      disabled={busy}
                      className={clsx(
                        'px-2.5 py-1 rounded-pill border text-[11px] font-sans transition-colors',
                        active
                          ? 'bg-ink1 border-ink1 text-card font-medium'
                          : 'bg-card border-rule text-ink2',
                      )}
                    >
                      {a.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {error ? <div className="text-[13px] text-tier-reported font-sans">{error}</div> : null}

            <Button block type="submit" loading={busy}>
              Add to garage
            </Button>
          </>
        ) : null}
      </form>

      <CarPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePick}
        onSelectCustom={handleManualEntry}
      />
    </div>
  );
}
